import { getInitDataRaw } from './telegram';

const BASE = `${import.meta.env.VITE_BACKEND_URL ?? ''}/api/client-app`;

export const CLIENT_API_INTERACTIVE_TIMEOUT_MS = 10_000;

export class ClientApiError extends Error {
  constructor(public status: number, public body: string, public path: string) {
    super(`Client API ${status} on ${path}: ${body || '(empty)'}`);
    this.name = 'ClientApiError';
  }
}

export class ClientApiTimeoutError extends Error {
  constructor(public timeoutMs: number, public path: string) {
    super(`Client API request timed out after ${timeoutMs}ms on ${path}`);
    this.name = 'ClientApiTimeoutError';
  }
}

export type ClientApiRequestInit = RequestInit & {
  timeoutMs?: number;
};

export async function clientApi<T>(path: string, opts: ClientApiRequestInit = {}): Promise<T> {
  const initData = getInitDataRaw();
  const customHeaders = new Headers(opts.headers);
  customHeaders.set('X-Telegram-Init-Data', initData);

  const hasBody = opts.body !== undefined && opts.body !== null;
  const isFormData = opts.body instanceof FormData;
  if (hasBody && !isFormData && !customHeaders.has('Content-Type')) {
    customHeaders.set('Content-Type', 'application/json');
  }

  const { timeoutMs, signal: callerSignal, ...requestOptions } = opts;
  const normalizedTimeoutMs = typeof timeoutMs === 'number' && Number.isFinite(timeoutMs) && timeoutMs > 0
    ? Math.floor(timeoutMs)
    : null;
  let requestSignal = callerSignal ?? undefined;
  let timeoutController: AbortController | null = null;
  let timeoutId: number | null = null;
  let timedOut = false;
  let abortFromCaller: (() => void) | null = null;

  if (normalizedTimeoutMs !== null) {
    timeoutController = new AbortController();
    requestSignal = timeoutController.signal;
    abortFromCaller = () => timeoutController?.abort(callerSignal?.reason);

    if (callerSignal?.aborted) {
      abortFromCaller();
    } else {
      callerSignal?.addEventListener('abort', abortFromCaller, { once: true });
      timeoutId = window.setTimeout(() => {
        if (timeoutController?.signal.aborted) return;
        timedOut = true;
        timeoutController?.abort(new DOMException('Request timed out', 'TimeoutError'));
      }, normalizedTimeoutMs);
    }
  }

  try {
    const res = await fetch(`${BASE}${path}`, {
      ...requestOptions,
      headers: customHeaders,
      signal: requestSignal,
    });

    const readText = async () => {
      try {
        return await res.text();
      } catch (error) {
        if (requestSignal?.aborted) throw error;
        return '';
      }
    };

    if (!res.ok) {
      const text = await readText();
      throw new ClientApiError(res.status, text, path);
    }

    const text = await readText();
    if (!text.trim()) return undefined as T;
    return JSON.parse(text) as T;
  } catch (error) {
    if (timedOut && normalizedTimeoutMs !== null) {
      throw new ClientApiTimeoutError(normalizedTimeoutMs, path);
    }
    throw error;
  } finally {
    if (timeoutId !== null) window.clearTimeout(timeoutId);
    if (callerSignal && abortFromCaller) {
      callerSignal.removeEventListener('abort', abortFromCaller);
    }
  }
}
